import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Modal, Segmented, Spin, Switch, Tabs} from 'antd';
import dayjs from 'dayjs';
import LightweightCandlestickChart, {KLineBar} from '../../../components/charts/LightweightCandlestickChart';
import {klineBarsFromCandles} from '../../../components/charts/KLineChart';
import {tradingService} from '../../../services/tradingService';
import {getHedgePositionKline} from '../../../services/hedgeService';
import type {HedgeLegRealized, HedgePosition} from '../../../types/hedge';

type Market = 'SPOT' | 'SWAP';

const TIMEFRAME_OPTIONS = [
    {label: '5m', value: '5m'},
    {label: '1H', value: '1h'},
    {label: '4H', value: '4h'},
    {label: '1D', value: '1d'},
];

const KLINE_LIMIT = 200;
const CHART_HEIGHT = 460;

interface Props {
    visible: boolean;
    onClose: () => void;
    /** 当前持仓（双腿 instId/均价/开仓时间/实时价来源；结束态含分腿已实现明细） */
    position: HedgePosition | null;
    /** 点击哪条腿的 K 线按钮就以哪个 market 作为默认 Tab */
    initialMarket: Market;
    /** 结束态：读离线 K 线快照（不再请求实时行情） */
    ended?: boolean;
}

interface MarketMeta {
    market: Market;
    instId?: string;
    avgPx?: number;
    closePx?: number;
    openedAtMs?: number;
    closedAtMs?: number;
    lastPx?: number;
    posSide: string;
}

const toTimestamp = (value?: string | null): number | undefined => {
    if (!value) return undefined;
    const parsed = dayjs(String(value).trim().replace('T', ' ').replace(/\.\d+$/, '').slice(0, 19));
    return parsed.isValid() ? parsed.valueOf() : undefined;
};

/**
 * 对冲腿 K 线弹窗：双 Tab（现货/永续）+ 双图叠加，
 * 运行中读实时行情；结束态读离线快照（开仓/平仓价线与持仓区间同时标出）。
 */
export const HedgeLegChartModal: React.FC<Props> = ({visible, onClose, position, initialMarket, ended}) => {
    const [market, setMarket] = useState<Market>(initialMarket);
    const [timeframe, setTimeframe] = useState('1h');
    const [overlay, setOverlay] = useState(false);
    const [barsByMarket, setBarsByMarket] = useState<Record<Market, KLineBar[]>>({SPOT: [], SWAP: []});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const fetchSeqRef = useRef(0);

    const spotInstId = position?.spotInstId;
    const swapInstId = position?.swapInstId;
    const spotLeg = position?.spotLeg;
    const swapLeg = position?.swapLeg;
    const endedCloseAt = position?.closedAt ?? position?.compensatedAt;

    // 行情展示元数据（结束态：开/平均价与开/平时间取分腿已实现明细，缺失回退仓位级字段）
    const metaByMarket = useMemo<Record<Market, MarketMeta>>(() => {
        const build = (leg: HedgeLegRealized | undefined, marketKey: Market,
                       instId: string | undefined, avgPx: number | undefined,
                       lastPx: number | undefined, posSide: string): MarketMeta => ({
            market: marketKey,
            instId,
            avgPx: leg?.openAvgPx ?? avgPx,
            closePx: leg?.closeAvgPx,
            openedAtMs: toTimestamp(leg?.openedAt) ?? toTimestamp(position?.createdAt),
            closedAtMs: toTimestamp(leg?.closedAt) ?? toTimestamp(endedCloseAt),
            lastPx,
            posSide,
        });
        return {
            SPOT: build(spotLeg, 'SPOT', spotInstId, position?.spotAvgPx,
                position?.spotLastPx, 'long'),
            SWAP: build(swapLeg, 'SWAP', swapInstId, position?.swapAvgPx,
                position?.swapLastPx, 'short'),
        };
    }, [spotLeg, swapLeg, spotInstId, swapInstId, position?.spotAvgPx, position?.swapAvgPx,
        position?.spotLastPx, position?.swapLastPx, position?.createdAt, endedCloseAt]);

    // 仅 instId 参与取数依赖，避免实时价每 10 秒刷新导致 K 线重复请求
    const instIdByMarket = useMemo<Record<Market, string | undefined>>(
        () => ({SPOT: spotInstId, SWAP: swapInstId}),
        [spotInstId, swapInstId]
    );

    // 每次打开/切换持仓：默认回到被点击的腿
    useEffect(() => {
        if (visible) {
            setMarket(initialMarket);
            setError('');
        }
    }, [visible, initialMarket, position?.id]);

    useEffect(() => {
        if (!visible) {
            setBarsByMarket({SPOT: [], SWAP: []});
            setError('');
        }
    }, [visible]);

    const loadMarket = useCallback(async (target: Market, seq: number)
        : Promise<{ bars: KLineBar[]; note: string }> => {
        const instId = instIdByMarket[target];
        if (!instId) {
            return {bars: [], note: ''};
        }
        if (ended && null != position?.id) {
            const snapshot = await getHedgePositionKline(position.id, target, timeframe);
            if (seq !== fetchSeqRef.current) {
                return {bars: [], note: ''};
            }
            return {bars: klineBarsFromCandles(snapshot.candles ?? []), note: snapshot.reason ?? ''};
        }
        const data = await tradingService.getGlobalDisplayCompleteChartData({
            instId,
            timeframe,
            limit: KLINE_LIMIT,
        });
        if (seq !== fetchSeqRef.current) {
            return {bars: [], note: ''};
        }
        return {bars: klineBarsFromCandles(data?.candles ?? []), note: ''};
    }, [instIdByMarket, timeframe, ended, position?.id]);

    useEffect(() => {
        if (!visible || !position) {
            return;
        }
        const seq = ++fetchSeqRef.current;
        setLoading(true);
        setError('');
        const targets: Market[] = overlay ? ['SPOT', 'SWAP'] : [market];
        Promise.all(targets.map(target => loadMarket(target, seq)))
            .then(results => {
                if (seq !== fetchSeqRef.current) {
                    return;
                }
                const next: Partial<Record<Market, KLineBar[]>> = {};
                const notes: Partial<Record<Market, string>> = {};
                targets.forEach((target, index) => {
                    next[target] = results[index].bars;
                    notes[target] = results[index].note;
                });
                setBarsByMarket(prev => ({...prev, ...next}));
                if (0 === (next[market]?.length ?? 0)) {
                    setError(notes[market] || (ended ? '该仓无 K 线快照' : '未获取到K线数据'));
                }
            })
            .catch((e: any) => {
                if (seq !== fetchSeqRef.current) {
                    return;
                }
                setError(e?.message ?? 'K线数据加载失败');
            })
            .finally(() => {
                if (seq === fetchSeqRef.current) {
                    setLoading(false);
                }
            });
    }, [visible, position?.id, market, timeframe, overlay, loadMarket, ended]);

    const currentMeta = metaByMarket[market];
    const currentBars = barsByMarket[market] ?? [];
    const comparisonBars = overlay
        ? (barsByMarket['SPOT' === market ? 'SWAP' : 'SPOT'] ?? [])
        : undefined;
    // 结束态：开仓价/平仓价 + 开仓时间/平仓时间（图表据此绘制持仓盈亏区间）；运行中只标开仓线与实时价
    const referenceLines = ended
        ? {
            avgPx: currentMeta.avgPx,
            closePx: currentMeta.closePx,
            cTime: currentMeta.openedAtMs,
            uTime: currentMeta.closedAtMs,
            posSide: currentMeta.posSide,
        }
        : {
            avgPx: currentMeta.avgPx,
            cTime: currentMeta.openedAtMs,
            posSide: currentMeta.posSide,
        };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1080}
            destroyOnClose
            title={position
                ? `${position.symbol} 双腿 K 线${ended ? '（离线快照）' : ''}`
                : '双腿 K 线'}
        >
            <div className="hedge-chart-toolbar">
                <Segmented
                    options={TIMEFRAME_OPTIONS}
                    value={timeframe}
                    onChange={value => setTimeframe(String(value))}
                />
                <span className="hedge-chart-switch">
                    <Switch size="small" checked={overlay} onChange={setOverlay}/>
                    <span>双图叠加</span>
                </span>
                <span className="hedge-chart-hint">
                    {ended ? '离线快照数据 · 不随行情刷新' : '滚轮缩放，拖动平移'}
                </span>
            </div>

            <Tabs
                activeKey={market}
                onChange={key => setMarket('SWAP' === key ? 'SWAP' : 'SPOT')}
                items={(['SPOT', 'SWAP'] as Market[]).map(target => ({
                    key: target,
                    label: `${'SPOT' === target ? '现货' : '永续'} ${metaByMarket[target].instId ?? '--'}`,
                    children: null,
                }))}
            />

            <div className="hedge-chart-body">
                {loading ? <div className="hedge-chart-mask"><Spin/></div> : null}
                {!loading && error ? (
                    <div className="hedge-chart-mask">
                        <Alert type="warning" showIcon message={error}/>
                    </div>
                ) : null}
                {!loading && !error && 0 === currentBars.length ? (
                    <div className="hedge-chart-mask hedge-chart-empty">暂无K线数据</div>
                ) : null}
                {0 < currentBars.length ? (
                    <LightweightCandlestickChart
                        key={`${market}-${timeframe}-${overlay ? 'overlay' : 'single'}`}
                        data={currentBars}
                        comparisonData={comparisonBars}
                        timeFrame={timeframe}
                        height={CHART_HEIGHT}
                        maxVisibleBars={KLINE_LIMIT}
                        markPrice={ended ? null : (currentMeta.lastPx ?? null)}
                        markPriceColor="#F0B429"
                        referenceLines={referenceLines}
                    />
                ) : null}
            </div>

            {overlay && currentBars.length > 0 && (comparisonBars?.length ?? 0) > 0 ? (
                <div className="hedge-chart-legend">
                    <span className="hedge-chart-legend-item main">主图：{currentMeta.instId}</span>
                    <span className="hedge-chart-legend-item compare">
                        叠加：{metaByMarket['SPOT' === market ? 'SWAP' : 'SPOT'].instId}
                    </span>
                </div>
            ) : null}
        </Modal>
    );
};

export default HedgeLegChartModal;
